"use client";

import React from "react";
import { useAppRouter } from "@/src/shared/hooks/useAppRouter";
import ListingSlider from "@/src/shared/components/listing/ListingSlider";
import NotFoundComponent from "@/src/shared/components/NotFoundComponent";
import HostDetailsSkeleton from "@/src/features/host/components/HostDetailsSkeleton";
import { useHostWithListings } from "../hooks/useHostWithListings";
import { hostService } from "../services";
import HostProfileSection from "./HostProfileSection";

const HostDetails: React.FC = () => {
  const router = useAppRouter();
  const hostId = hostService.parseHostId(router.query.host_id);

  const {
    hostDetails,
    listings,
    meta,
    isLoading,
    error: hookError,
  } = useHostWithListings(hostId ?? 0, { page: 1, limit: 50 });

  if (!router.isReady || hostId === null) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        <HostDetailsSkeleton />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        <HostDetailsSkeleton />
      </div>
    );
  }

  if (hookError) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-ink-black mb-4">
            Something went wrong
          </h1>
          <p className="text-ink-secondary mb-6 text-sm sm:text-base">
            Failed to load host details
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-coral-pink-500 text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-coral-pink-600 transition-colors text-sm sm:text-base"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!hostDetails) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        <NotFoundComponent message="Sorry, the host you are looking for does not exist or is unavailable." />
      </div>
    );
  }

  const transformedListings = listings.map((listing) => ({ listing }));
  const chunkedListings = hostService.chunk(transformedListings, 15);

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
        {/* Host Profile Section */}
        <HostProfileSection hostDetails={hostDetails} />

        {/* Host Listings Section */}
        <div className="space-y-12 sm:space-y-16">
          {listings && listings.length > 0 ? (
            chunkedListings.map((chunk, idx) => (
              <div key={idx} className="mb-8">
                <ListingSlider
                  title={
                    idx === 0
                      ? `Properties by ${hostService.fullName(hostDetails)}`
                      : ""
                  }
                  listings={chunk}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-12 sm:py-16">
              <div className="bg-gray-50 rounded-2xl p-8 sm:p-12">
                <h3 className="text-lg sm:text-xl font-semibold text-ink-black mb-3 sm:mb-4">
                  No Properties Available
                </h3>
                <p className="text-ink-secondary text-sm sm:text-base">
                  This host doesn't have any active properties at the moment.
                </p>
              </div>
            </div>
          )}
          {/* Pagination Info */}
          {meta && meta.pagination && listings && listings.length > 0 && (
            <div className="text-center mt-6 sm:mt-8">
              <p className="text-ink-secondary text-sm sm:text-base">
                Showing {listings.length} of {meta.pagination.total_count}{" "}
                properties
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HostDetails;
